import {
  TaskCategoryStage,
  TaskCategoryTaskType,
  AutopilotTaskDto,
  TaskStepDto,
  TaskCategory,
  TaskCategoryWorkflowType,
} from '@urbanx/agx.activities.client'
import {
  IconTaskType,
  IconTaskTypes,
  BadgeColor,
  AgxBadge,
  AgxCheckbox,
  AgxCollapsible,
  AgxIconTask,
  AgxProgressBar,
  AgxSelectChip,
  AgxTypography,
  getDateStatus,
  formatDate,
  AgxEmptyState,
  Range,
  AgxSpacer,
} from '@urbanx/agx.ui-components2'
import { TaskBlocker } from 'pages/allTasks/TaskBlockers'
import { splitPascalCase } from 'utils/stringUtils'

const taskIconMap: Partial<Record<TaskCategoryTaskType, IconTaskType>> = {
  [TaskCategoryTaskType.NewListing]: IconTaskTypes.NewListing,
  [TaskCategoryTaskType.ContractCompliance]: IconTaskTypes.AgreementCompliance,
  [TaskCategoryTaskType.Settlement]: IconTaskTypes.Settlement,
  [TaskCategoryTaskType.SettlementPaymentsReview]: IconTaskTypes.Settlement,
  [TaskCategoryTaskType.SettlementPayments]:
    IconTaskTypes.ApproveSettlementTransfers,
  [TaskCategoryTaskType.Termination]: IconTaskTypes.Terminate,
  [TaskCategoryTaskType.TerminationPaymentsReview]: IconTaskTypes.Terminate,
  [TaskCategoryTaskType.Withdrawn]: IconTaskTypes.Withdrawn,
  [TaskCategoryTaskType.TenancyInformation]: IconTaskTypes.Tenanted,
  [TaskCategoryTaskType.SellerDisclosureVendorSolicitorUpload]: IconTaskTypes.SellerDisclosure,
  [TaskCategoryTaskType.HolidayRental]: IconTaskTypes.HolidayRental,
  [TaskCategoryTaskType.OwnerBuilderDeclaration]: IconTaskTypes.OwnerBuilder,
  [TaskCategoryTaskType.Launch]: IconTaskTypes.Launch,
  [TaskCategoryTaskType.CoolingOffPeriod]:
    IconTaskTypes.FollowupOutstandingContractInformation,
  [TaskCategoryTaskType.FinanceDue]: IconTaskTypes.FollowupMarketingfunds,
  [TaskCategoryTaskType.BuildingAndPestDue]: IconTaskTypes.OwnerBuilder,
  [TaskCategoryTaskType.Unconditional]: IconTaskTypes.Unconditional,
}

export const mapTaskTypetoIconType = (
  taskType?: TaskCategoryTaskType,
  fallback: IconTaskType = IconTaskTypes.NewListing
): IconTaskType => taskIconMap[taskType as TaskCategoryTaskType] ?? fallback

export const getStatusBadgeColor = (inProgress: boolean, isPending: boolean) : BadgeColor => {
  if (isPending) return 'red'
  return inProgress ? 'blue' : 'green'
}

export const renderDueDateBadge = (dueDate: string, isCompleted: boolean) => {
  const status = isCompleted ? 'Completed' : getDateStatus(dueDate ?? '')
  const isHighlighted = isCompleted || ['Today', 'Overdue'].includes(status)
  const badgeColor = (status === 'Overdue' ? 'red' : 'yellow') as BadgeColor

  if (status === 'Invalid date') {
    return (
      <AgxBadge type="colourfill" colour="red" size="md" text="Invalid date" />
    )
  }

  const date = `${formatDate(dueDate ?? '', false)}`
  const badgeText = isCompleted ? `${date}` : `Due ${date}`

  if (isHighlighted) {
    return (
      <AgxBadge
        type="highlighted"
        colour={badgeColor}
        size="md"
        text={badgeText}
        highlightText={status}
      />
    )
  }

  return (
    <AgxBadge
      type="colourfill"
      colour={badgeColor}
      size="md"
      text={badgeText}
    />
  )
}

const getStepDueDate = (step: TaskStepDto): string | undefined => {
  // Allow forward-compatible payloads where due date is included on step or step data.
  const stepWithDueDate = step as TaskStepDto & {
    dueDate?: Date | string
    data?: TaskStepDto['data'] & { dueDate?: Date | string }
  }

  const rawDueDate = stepWithDueDate.dueDate ?? stepWithDueDate.data?.dueDate
  if (!rawDueDate) return undefined

  const date = rawDueDate instanceof Date ? rawDueDate : new Date(rawDueDate)
  if (Number.isNaN(date.getTime())) return undefined

  return date.toISOString()
}

export const getStepCompletionPercentage = (
  steps: TaskStepDto[]
): Range<0, 100> => {
  if (steps.length === 0) return 0
  const completed = steps.filter((t) => t.isCompleted).length
  return Math.round((completed / steps.length) * 100) as Range<0, 100>
}

export enum WorkflowStageIndicatorStatus {
  COMPLETED = 'COMPLETED',
  INPROGRESS = 'INPROGRESS',
  ISPENDING = 'ISPENDING',
  LOCKED = 'LOCKED',
}
export interface WorkflowStageIndicator {
  name: string
  color: BadgeColor
  status: WorkflowStageIndicatorStatus
  stage?: TaskCategoryStage
}

const stageOrder = Object.values(TaskCategoryStage)

const campaignStages = stageOrder.slice(0,
  stageOrder.indexOf(TaskCategoryStage.Withdrawn) + 1
)
const campaignBeginningStages = [TaskCategoryStage.AgreementCompliance]

const contractStages = stageOrder.slice(
  stageOrder.indexOf(TaskCategoryStage.ContractCompliance),
  stageOrder.indexOf(TaskCategoryStage.Settlement) + 1
)
const contractBeginningStages = [
  TaskCategoryStage.ContractCompliance,
  TaskCategoryStage.ContractRequest,
]

const getWorkflowStageConfig = (stage?: TaskCategoryStage)
  : { beginningStages: TaskCategoryStage[] } => {
  if (stage && contractStages.includes(stage))
    return { beginningStages: contractBeginningStages }
  if (stage && campaignStages.includes(stage))
    return { beginningStages: campaignBeginningStages }

  return { beginningStages: campaignBeginningStages }
}

const progressionPalette: BadgeColor[] = ['blue', 'purple', 'orange', 'teal']
const grey : BadgeColor = 'grey'
const lightGrey: BadgeColor = 'lightgrey'

const getWorkflowStageColorByOrder = (
  stage: TaskCategoryStage,
  orderedWorkflowStages: TaskCategoryStage[],
  isBeginningStage: boolean
): BadgeColor => {
  const stageIndex = orderedWorkflowStages.indexOf(stage)

  const lastStageIndex = orderedWorkflowStages.length - 1
  if (lastStageIndex < 0 || stageIndex < 0)
    return progressionPalette[stageIndex]

  if (stageIndex === lastStageIndex)
    return 'green' as BadgeColor

  const computedIndex = isBeginningStage ? stageIndex : stageIndex + 1

  return progressionPalette[computedIndex] ??
    progressionPalette[progressionPalette.length - 1]
}

const getUniqueOrderedWorkflowStages = (
  orderedTasks: Required<AutopilotTaskDto>[]
): TaskCategoryStage[] =>
  Array.from(
    new Set(
      orderedTasks
        .map((task) => task.category?.stage)
        .filter((stage): stage is TaskCategoryStage => Boolean(stage))
    )
  )

export const getWorkflowBadges = (
  tasks: Required<AutopilotTaskDto>[]
): WorkflowStageIndicator[] => {
  const withdrawnTask = tasks.find(
    (task) => task.category?.stage === TaskCategoryStage.Withdrawn
  )
  const terminatedTask = tasks.find(
    (task) => task.category?.stage === TaskCategoryStage.Termination
  )

  const orderedTasks = [...tasks].sort((a, b) => a.order - b.order)

  const taskInProgress = orderedTasks.find((task) => task.inProgress)
  const firstTaskReadyToProcess = orderedTasks.find(
    (task) => !task.isCompleted && !task.isLocked
  )
  const allCompleted = orderedTasks.length > 0 && orderedTasks.every((task) => task.isCompleted)

  const currentTask = taskInProgress ?? firstTaskReadyToProcess
  const currentStage = currentTask?.category?.stage
  const { beginningStages } = getWorkflowStageConfig(
    currentStage ?? orderedTasks[0]?.category.stage
  )
  const orderedWorkflowStages = getUniqueOrderedWorkflowStages(orderedTasks)
  const currentStageIndex = currentStage
    ? orderedWorkflowStages.indexOf(currentStage)
    : allCompleted
      ? orderedWorkflowStages.length
      : -1

  const getNonInProgressWorkflowBadge = (
    name: string,
    stage: TaskCategoryStage,
    status: WorkflowStageIndicatorStatus
  ): WorkflowStageIndicator =>
    ({
      name,
      stage,
      color: status === WorkflowStageIndicatorStatus.COMPLETED ? lightGrey : grey,
      status,
    })

  const getWorkflowStageLabel = (type?: TaskCategoryStage) => splitPascalCase(type)

  const isWithdrawnOrTerminated = Boolean(withdrawnTask || terminatedTask)

  const withdrawnOrTerminatedCompleted = withdrawnTask?.isCompleted || terminatedTask?.isCompleted
  const isWithdrawalOrTerminationStage = (taskStage: TaskCategoryStage) =>
    taskStage === TaskCategoryStage.Withdrawn ||
    taskStage === TaskCategoryStage.Termination


  return [
    ...orderedWorkflowStages.map((taskStage, index) => {

      // Handle withdrawn/terminated stages specially
      if (isWithdrawnOrTerminated && isWithdrawalOrTerminationStage(taskStage)) {
        return {
          status: withdrawnOrTerminatedCompleted
            ? WorkflowStageIndicatorStatus.COMPLETED
            : WorkflowStageIndicatorStatus.INPROGRESS,
          stage: taskStage,
          name: getWorkflowStageLabel(taskStage),
          color: (
            withdrawnOrTerminatedCompleted
              ? lightGrey
              : getWorkflowStageColorByOrder(
                  taskStage,
                  orderedWorkflowStages,
                  beginningStages.includes(taskStage)
                )
          ) as BadgeColor,
        }
      }

      if (currentTask?.category?.stage === taskStage && !isWithdrawnOrTerminated) {
        return {
          status: WorkflowStageIndicatorStatus.INPROGRESS,
          stage: taskStage,
          name: getWorkflowStageLabel(taskStage),
          color: getWorkflowStageColorByOrder(
            taskStage,
            orderedWorkflowStages,
            beginningStages.includes(taskStage)
          ),
        }
      }

      // Determine status for other stages
      const status = isWithdrawnOrTerminated
        ? WorkflowStageIndicatorStatus.COMPLETED
        : currentStageIndex === -1
          ? WorkflowStageIndicatorStatus.LOCKED
          : index < currentStageIndex
            ? WorkflowStageIndicatorStatus.COMPLETED
            : WorkflowStageIndicatorStatus.LOCKED

      return getNonInProgressWorkflowBadge(
        getWorkflowStageLabel(taskStage),
        taskStage,
        status
      )
    }),
    {
      name: 'Complete',
      color: allCompleted ? grey : lightGrey,
      status: WorkflowStageIndicatorStatus.LOCKED,
    },
  ]
}

export interface StepsCompletionResult {
  allStepsCompleted: boolean
  latestCompletedDate?: Date
}

export function getStepsCompletionInfo(
  tasks: AutopilotTaskDto[]
): StepsCompletionResult {
  const allSteps = tasks.flatMap((task) => task.taskSteps ?? [])

  const allStepsCompleted =
    allSteps.length > 0 && allSteps.every((step) => step.isCompleted === true)

  const latestCompletedDate = allSteps
    .filter((step) => step.isCompleted === true && step.completedDate !== undefined)
    .reduce<Date | undefined>((latest, step) => {
      if (!latest) return step.completedDate
      return step.completedDate! > latest ? step.completedDate : latest
    }, undefined)

  return { allStepsCompleted, latestCompletedDate }
}

type AssignTaskHandler = (payload: {
  userId?: string
  campaignId?: string
  contractId?: string
  category?: TaskCategory
}) => void

type ToggleTaskStepsHandler = (payload: {
  stepIds: number[]
  workflowType: TaskCategoryWorkflowType
  category?: TaskCategory
  campaignId?: string
  contractId?: string
}) => void

type AssigneeOption = {
  label: string
  value: string
  selected?: boolean
}

export const renderTaskCollapsible = ({
  task,
  contractId,
  assigneeOptions,
  assignTask,
  toggleTaskSteps,
}: {
  task: AutopilotTaskDto
  contractId?: string
  assigneeOptions: AssigneeOption[]
  assignTask: AssignTaskHandler
  toggleTaskSteps: ToggleTaskStepsHandler
}) => {
  if (!task.category) return null

  const { campaignId } = task
  const taskSteps = task.taskSteps ?? []
  const allTasksCompleted =
    taskSteps.length > 0 &&
    taskSteps.every((step) => step.isCompleted === true)

  return (
    <AgxCollapsible
      key={task.category?.taskType}
      open={task.isCompleted ? false : true}
      iconPosition="none"
      enableToggle={true}
      variant={'withoutDivider'}
      title={''}
      customTitle={
        <AgxSpacer direction="vertical" fill
          gap="spacing-ml" padding={{ left: 'spacing-xl', right: 'spacing-xl' }}
        >
          <AgxSpacer direction='horizontal' fill contentAlignment="center" contentJustification="space-between">
            <AgxSpacer direction='horizontal' gap="spacing-lg" contentAlignment="center">
              <AgxIconTask
                type={mapTaskTypetoIconType(task.category?.taskType)}
                iconColor="black"
                height={32}
                width={32}
                bgColor="var(--bg-progress)"
              />
              <AgxTypography id={'title'} type={'heading'} size={'medium'}>
                {splitPascalCase(task.category?.taskType || '')}
              </AgxTypography>
            </AgxSpacer>
            <div onClick={(e) => e.stopPropagation()}>
              <AgxSpacer direction='horizontal' gap="spacing-lg" contentAlignment="center">
                <AgxSelectChip
                  id={
                    task.category?.taskType ?? `assignToUserChip-${campaignId}`
                  }
                  variant="icon"
                  label={''}
                  onSelect={(options) => {
                    const selectedValue = options.find(
                      (option) => option.selected
                    )?.value
                    if (task.campaignId && task.category?.taskType) {
                      assignTask({
                        userId:
                          selectedValue === 'Unassigned'
                            ? undefined
                            : selectedValue,
                        campaignId,
                        contractId,
                        category: task.category,
                      })
                    }
                  }}
                  size={'medium'}
                  type={'single-select'}
                  defaultSelection={task.assignedTo}
                  options={!allTasksCompleted ? assigneeOptions : []}
                  searchPlaceHolder="Search"
                  align="right"
                  hideFooter
                />
                {renderDueDateBadge(
                  (task.dueDate ?? '') as string,
                  taskSteps.every((step) => step.isCompleted)
                )}
              </AgxSpacer>
            </div>
          </AgxSpacer>
          {task.isCompleted ? (
            <AgxTypography
              id={'txtComplete'}
              type={'caption'}
              size={'medium'}
              color="var(--text-success)"
            >
              Complete! 🎉
            </AgxTypography>
          ) : (
            <AgxProgressBar
              variant="slim"
              id="progress"
              percentageProgressPosition="left"
              mode="dark"
              progress={getStepCompletionPercentage(taskSteps)}
            />
          )}
        </AgxSpacer>
      }
      content={
        <AgxSpacer direction='horizontal' fill>
          <AgxSpacer direction='vertical' fill gap="spacing-2xl">
            {(!task.assignedTo || task.assignedTo === 'Unassigned') && !task.isPending ?
              <AgxSpacer direction='horizontal' fill contentAlignment="center" contentJustification="center">
                <AgxEmptyState
                  variant="advanced"
                  glyphType="locked"
                  actionButtons={[]}
                  label="Assign task to unlock"
                />
              </AgxSpacer> :
              <TaskBlocker task={task}>
                <AgxSpacer direction='vertical' fill gap="spacing-lg">
                  {taskSteps.map((step) => {
                    const labelText = step.name ?? ''
                    const badgeMessage = step.data?.badgeMessage?.trim()
                    const stepDueDate = getStepDueDate(step)
                    const hasHelperContent =
                      Boolean(step.data?.text) || Boolean(badgeMessage) || Boolean(stepDueDate)

                    return (
                      <div
                        key={`${step.order}-${step.name}`}
                        className={`taskStep-container taskStep-item${allTasksCompleted ? ' disabled' : ''}`}
                      >
                        <AgxCheckbox
                          id={`${step.order}-${step.name}`}
                          checked={!!step.isCompleted}
                          onChange={() =>
                            toggleTaskSteps({
                              stepIds: [step.order!],
                              workflowType: task.category
                                ?.workflowType as TaskCategoryWorkflowType,
                              category: task.category,
                              campaignId,
                              contractId,
                            })
                          }
                          strikethroughOnCheck
                          label={labelText}
                          disabled={allTasksCompleted}
                        />
                        {hasHelperContent && (
                          <div className="taskStep-helperContent">
                              <AgxTypography
                                id={`task-step-helper-${step.order}`}
                                key={`task-step-helper-${step.order}`}
                                type={'body_ui_strong'}
                                size={'medium'}
                                color="var(--text-primary)"
                              >
                                {step.data?.text}
                              </AgxTypography>
                            {stepDueDate && (
                              <div className="taskStep-dueDateBadge">
                                {renderDueDateBadge(
                                  stepDueDate,
                                  step.isCompleted ?? false
                                )}
                              </div>
                            )}
                            {badgeMessage && (
                              <div className="taskStep-helperBadge">
                                <AgxBadge
                                  type="colourfill"
                                  colour={grey}
                                  size="sm"
                                  text={badgeMessage}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </AgxSpacer>
              </TaskBlocker>
            }
          </AgxSpacer>
        </AgxSpacer>
      }
    />
  )
}
